import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/components/SmoothScroll";

const LINKS = [
  { label: "Services", id: "services" },
  { label: "Work", id: "work" },
  { label: "Process", id: "process" },
  { label: "Contact", id: "contact" },
];

export function LogoMark({ className = "w-8 h-8" }) {
  return <img src="/logo-mark.png" alt="" aria-hidden="true" className={`${className} object-contain`} />;
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
  }, [open]);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#070707]/70 backdrop-blur-xl border-b border-white/5" : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-10 py-4">
          <button
            data-testid="nav-logo"
            onClick={() => window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center gap-3 group"
            aria-label="Southline Creative — home"
          >
            <LogoMark className="w-9 h-9 transition-transform duration-500 group-hover:rotate-6" />
            <span className="font-display text-xl tracking-wide">
              Southline <span className="italic text-gold">Creative</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-[13px] uppercase tracking-[0.2em] text-cream/70 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => go("contact")}
              className="btn-shine bg-gold text-ink text-[13px] uppercase tracking-[0.2em] font-medium px-6 py-3 hover:bg-gold-soft transition-colors duration-300"
            >
              Start a project
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-cream p-2"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-[#070707]/95 backdrop-blur-xl md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-between px-6 py-4">
              <span className="font-display text-xl">Southline <span className="italic text-gold">Creative</span></span>
              <button data-testid="mobile-menu-close" className="p-2 text-cream" onClick={() => setOpen(false)} aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="px-8 pt-14 flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-menu-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.id)}
                  className="text-left font-display text-5xl py-3 text-cream hover:text-gold transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                onClick={() => go("contact")}
                className="mt-10 btn-shine bg-gold text-ink uppercase tracking-[0.2em] text-sm font-medium px-8 py-4 flex items-center gap-2 w-fit"
              >
                Start a project <ArrowUpRight className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
